import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Play, ExternalLink } from 'lucide-react';
import type { Call, CallImpactResponse } from '../types';
import { STAGE_OPTIONS } from '../services/api';
import './CallsTable.css';

function formatDuration(seconds: number | null): string {
  if (!seconds) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

type BadgeVariant = 'success' | 'danger' | 'warning' | 'neutral' | 'deep-green';

interface BadgeProps {
  text: string;
  variant: BadgeVariant;
}

function Badge({ text, variant }: BadgeProps) {
  return <span className={`badge badge-${variant}`}>{text}</span>;
}

interface CallRowProps {
  call: Call;
  onViewLoan: (loanId: string) => void;
}

function CallRow({ call, onViewLoan }: CallRowProps) {
  const [expanded, setExpanded] = useState(false);
  const summary = call.call_summary;

  const statusVariant: BadgeVariant =
    call.call_status === 'completed' ? 'success' : call.call_status === 'missed' ? 'danger' : 'neutral';
  const interestVariant: BadgeVariant =
    call.is_interested === true ? 'success' : call.is_interested === false ? 'danger' : 'neutral';
  const qualifiedVariant: BadgeVariant = summary?.qualified ? 'success' : 'danger';
  const loanVariant: BadgeVariant =
    call.loan_status === 'Approved' ? 'deep-green' : call.loan_status === 'Active' ? 'success' : call.loan_status === 'Rejected' ? 'danger' : 'warning';

  return (
    <>
      <tr className="call-row" onClick={() => setExpanded(!expanded)}>
        <td>
          <span className="expand-icon">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </td>
        <td className="phone-cell">{call.phone_10 || call.phone_number}</td>
        <td><Badge text={call.call_status} variant={statusVariant} /></td>
        <td>{formatDuration(call.call_duration)}</td>
        <td>
          <Badge
            text={call.is_interested === true ? 'Yes' : call.is_interested === false ? 'No' : '-'}
            variant={interestVariant}
          />
        </td>
        <td>
          {summary ? (
            <Badge text={summary.qualified ? 'Yes' : 'No'} variant={qualifiedVariant} />
          ) : (
            '-'
          )}
        </td>
        <td><Badge text={call.loan_status || '-'} variant={loanVariant} /></td>
        <td>{summary?.interest_percentage != null ? `${summary.interest_percentage}%` : '-'}</td>
        <td className="time-cell">
          {new Date(call.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </td>
        <td>
          {call.loan_id ? (
            <button
              className="view-loan-btn"
              onClick={(e) => {
                e.stopPropagation();
                onViewLoan(call.loan_id);
              }}
            >
              <ExternalLink size={14} />
              View Loan
            </button>
          ) : (
            '-'
          )}
        </td>
      </tr>
      {expanded && summary && (
        <tr className="expanded-row">
          <td colSpan={10}>
            <div className="expanded-content">
              <div className="expanded-grid">
                <div className="expanded-section">
                  <h4>Qualification Details</h4>
                  <ul className="detail-list">
                    <li>Stage Reached: <strong>{summary.qualification_stage_reached || '-'}</strong></li>
                    <li>Has Own House: <strong>{summary.has_own_house ? 'Yes' : 'No'}</strong></li>
                    <li>Has Own Shop: <strong>{summary.has_own_shop ? 'Yes' : 'No'}</strong></li>
                    <li>Has Name Board: <strong>{summary.has_name_board ? 'Yes' : 'No'}</strong></li>
                    <li>Board Has User Name: <strong>{summary.board_has_user_name ? 'Yes' : 'No'}</strong></li>
                    <li>Has Udyam Aadhar: <strong>{summary.has_udyam_aadhar ? 'Yes' : 'No'}</strong></li>
                    <li>Has Electricity Bill: <strong>{summary.has_electricity_bill ? 'Yes' : 'No'}</strong></li>
                  </ul>
                </div>
                <div className="expanded-section">
                  <h4>Call Analysis</h4>
                  <ul className="detail-list">
                    {summary.call_analysis?.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  {summary.disqualification_reason && (
                    <p className="disqual-reason">
                      Disqualification: <strong>{summary.disqualification_reason}</strong>
                    </p>
                  )}
                  {summary.recording_s3_url && (
                    <div className="recording-section">
                      <h4>
                        <Play size={14} />
                        Call Recording
                      </h4>
                      <audio controls className="recording-player">
                        <source src={summary.recording_s3_url} type="audio/wav" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                </div>
                <div className="expanded-section expanded-section-wide">
                  <h4>Reasoning</h4>
                  <p className="reasoning-text">{summary.qualification_reasoning || summary.reasoning}</p>
                </div>
                {summary.user_statements?.length > 0 && (
                  <div className="expanded-section expanded-section-wide">
                    <h4>User Statements</h4>
                    <ul className="detail-list">
                      {summary.user_statements.map((stmt, i) => (
                        <li key={i}>"{stmt}"</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

interface CallsTableProps {
  data: CallImpactResponse;
  stage: string;
  onStageChange: (stage: string) => void;
}

export default function CallsTable({ data, stage, onStageChange }: CallsTableProps) {
  const navigate = useNavigate();
  const calls = data?.calls || [];

  const handleViewLoan = (loanId: string) => {
    navigate(`/loans/${loanId}`);
  };

  if (calls.length === 0) {
    return (
      <div className="table-card">
        <div className="table-header-row">
          <h3 className="table-title">Call Details</h3>
          <div className="stage-dropdown-wrapper">
            <label className="stage-label">Stage:</label>
            <select
              className="stage-select"
              value={stage}
              onChange={(e) => onStageChange(e.target.value)}
            >
              {STAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="no-data">No calls found for the selected date range.</p>
      </div>
    );
  }

  return (
    <div className="table-card">
      <div className="table-header-row">
        <h3 className="table-title">Call Details ({calls.length} calls)</h3>
        <div className="stage-dropdown-wrapper">
          <label className="stage-label">Stage:</label>
          <select
            className="stage-select"
            value={stage}
            onChange={(e) => onStageChange(e.target.value)}
          >
            {STAGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="table-wrapper">
        <table className="calls-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th>Phone</th>
              <th>Status</th>
              <th>Duration</th>
              <th>Interested</th>
              <th>Qualified</th>
              <th>Loan Status</th>
              <th>Interest %</th>
              <th>Time</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((call) => (
              <CallRow key={call.event_id} call={call} onViewLoan={handleViewLoan} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
